/**
 * Incident Cost Calculator Data
 *
 * Each incident has base costs that are modified by the user's response quality.
 * Response multipliers: Excellent (0.6x) → Good (0.8x) → Average (1.0x) → Poor (1.4x)
 */

export interface CostCategory {
  name: string;
  description: string;
  baseCost: number;
  isApplicable: boolean;
  isSharedCost?: boolean; // True for IR infrastructure costs (legal, detection, etc.) that are shared across incidents in a scenario
}

export interface IncidentCostBreakdown {
  incidentId: string;
  incidentTitle: string;
  categories: {
    // Primary Costs
    detectionAndEscalation: CostCategory;
    legal: CostCategory;
    notification: CostCategory;
    customerExperience: CostCategory;
    postBreachResponse: CostCategory;
    directLoss: CostCategory; // Theft, ransom, service abuse, etc.

    // Secondary Costs
    lostRevenue: CostCategory;
    liabilityAndFines: CostCategory;
    brandDamage: CostCategory;
  };
}

// Response score to multiplier mapping
export function getResponseMultiplier(responseScore: number): { multiplier: number; label: string } {
  if (responseScore >= 80) return { multiplier: 0.6, label: 'Excellent Response' };
  if (responseScore >= 60) return { multiplier: 0.8, label: 'Good Response' };
  if (responseScore >= 40) return { multiplier: 1.0, label: 'Average Response' };
  if (responseScore >= 20) return { multiplier: 1.2, label: 'Below Average Response' };
  return { multiplier: 1.4, label: 'Poor Response' };
}

// Calculate total cost with response multiplier
export function calculateIncidentCost(
  breakdown: IncidentCostBreakdown,
  responseScore: number
): {
  categories: Array<{
    name: string;
    description: string;
    baseCost: number;
    adjustedCost: number;
    isApplicable: boolean;
  }>;
  subtotalPrimary: number;
  subtotalSecondary: number;
  totalBaseCost: number;
  totalAdjustedCost: number;
  multiplier: number;
  multiplierLabel: string;
} {
  const { multiplier, label } = getResponseMultiplier(responseScore);

  const primaryCategories = [
    breakdown.categories.detectionAndEscalation,
    breakdown.categories.legal,
    breakdown.categories.notification,
    breakdown.categories.customerExperience,
    breakdown.categories.postBreachResponse,
    breakdown.categories.directLoss,
  ];

  const secondaryCategories = [
    breakdown.categories.lostRevenue,
    breakdown.categories.liabilityAndFines,
    breakdown.categories.brandDamage,
  ];

  const mapCategory = (cat: CostCategory) => ({
    name: cat.name,
    description: cat.description,
    baseCost: cat.baseCost,
    adjustedCost: cat.isApplicable ? Math.round(cat.baseCost * multiplier) : 0,
    isApplicable: cat.isApplicable,
  });

  const mappedPrimary = primaryCategories.map(mapCategory);
  const mappedSecondary = secondaryCategories.map(mapCategory);

  const subtotalPrimary = mappedPrimary.reduce((sum, c) => sum + c.adjustedCost, 0);
  const subtotalSecondary = mappedSecondary.reduce((sum, c) => sum + c.adjustedCost, 0);
  const totalBaseCost = [...primaryCategories, ...secondaryCategories]
    .filter(c => c.isApplicable)
    .reduce((sum, c) => sum + c.baseCost, 0);

  return {
    categories: [...mappedPrimary, ...mappedSecondary],
    subtotalPrimary,
    subtotalSecondary,
    totalBaseCost,
    totalAdjustedCost: subtotalPrimary + subtotalSecondary,
    multiplier,
    multiplierLabel: label,
  };
}

// Cost breakdowns for each incident
export const incidentCostData: Record<string, IncidentCostBreakdown> = {
  // ==========================================
  // LEGACY INCIDENTS (Project Sequoia)
  // ==========================================
  'incident-1': {
    incidentId: 'incident-1',
    incidentTitle: 'Sophisticated Phishing Attack Targeting Executives',
    categories: {
      detectionAndEscalation: {
        name: 'Detection & Escalation',
        description: 'Security team investigation, forensic analysis, threat hunting',
        baseCost: 13000,
        isApplicable: true,
      },
      legal: {
        name: 'Legal Counsel',
        description: 'External counsel for breach assessment and regulatory guidance',
        baseCost: 11000,
        isApplicable: true,
      },
      notification: {
        name: 'Notification',
        description: 'Board notification, internal communications',
        baseCost: 5000,
        isApplicable: true,
      },
      customerExperience: {
        name: 'Customer Experience',
        description: 'N/A - Internal incident, no customer impact',
        baseCost: 0,
        isApplicable: false,
      },
      postBreachResponse: {
        name: 'Post-Breach Response',
        description: 'Credential resets, MFA deployment, security awareness training',
        baseCost: 36000,
        isApplicable: true,
      },
      directLoss: {
        name: 'Direct Financial Loss',
        description: 'N/A - No confirmed credential compromise or theft',
        baseCost: 0,
        isApplicable: false,
      },
      lostRevenue: {
        name: 'Lost Revenue',
        description: 'Executive productivity loss, delayed strategic initiatives',
        baseCost: 25000,
        isApplicable: true,
      },
      liabilityAndFines: {
        name: 'Liability & Fines',
        description: 'N/A - No confirmed breach requiring disclosure',
        baseCost: 0,
        isApplicable: false,
      },
      brandDamage: {
        name: 'Brand & Reputation',
        description: 'N/A - Internal incident, not public',
        baseCost: 0,
        isApplicable: false,
      },
    },
  },

  'incident-2': {
    incidentId: 'incident-2',
    incidentTitle: 'Supply Chain Compromise Affecting Key Vendor',
    categories: {
      detectionAndEscalation: {
        name: 'Detection & Escalation',
        description: 'Threat hunting, IOC analysis, system-wide scanning',
        baseCost: 26000,
        isApplicable: true,
      },
      legal: {
        name: 'Legal Counsel',
        description: 'Vendor contract review, liability assessment, regulatory coordination',
        baseCost: 24000,
        isApplicable: true,
      },
      notification: {
        name: 'Notification',
        description: 'Customer notification, regulatory disclosure across jurisdictions',
        baseCost: 20000,
        isApplicable: true,
      },
      customerExperience: {
        name: 'Customer Experience',
        description: 'Customer support surge, service disruption communications',
        baseCost: 29000,
        isApplicable: true,
      },
      postBreachResponse: {
        name: 'Post-Breach Response',
        description: 'System remediation, vendor replacement evaluation, security hardening',
        baseCost: 105000,
        isApplicable: true,
      },
      directLoss: {
        name: 'Direct Financial Loss',
        description: 'Service disruption costs, emergency vendor migration',
        baseCost: 144000,
        isApplicable: true,
      },
      lostRevenue: {
        name: 'Lost Revenue',
        description: 'Business disruption during remediation period',
        baseCost: 83000,
        isApplicable: true,
      },
      liabilityAndFines: {
        name: 'Liability & Fines',
        description: 'Potential regulatory fines, customer compensation claims',
        baseCost: 33000,
        isApplicable: true,
      },
      brandDamage: {
        name: 'Brand & Reputation',
        description: 'Trust impact from third-party compromise, vendor management scrutiny',
        baseCost: 54000,
        isApplicable: true,
      },
    },
  },

  'incident-3': {
    incidentId: 'incident-3',
    incidentTitle: 'Ransomware Attack on Tucson Branch Office',
    categories: {
      detectionAndEscalation: {
        name: 'Detection & Escalation',
        description: 'Incident response team mobilization, forensic investigation',
        baseCost: 38000,
        isApplicable: true,
      },
      legal: {
        name: 'Legal Counsel',
        description: 'Ransom negotiation guidance, regulatory compliance, law enforcement coordination',
        baseCost: 33000,
        isApplicable: true,
      },
      notification: {
        name: 'Notification',
        description: 'Merchant notification, regulatory bodies, payment card networks',
        baseCost: 26000,
        isApplicable: true,
      },
      customerExperience: {
        name: 'Customer Experience',
        description: 'Merchant support, transaction processing alternatives',
        baseCost: 45000,
        isApplicable: true,
      },
      postBreachResponse: {
        name: 'Post-Breach Response',
        description: 'System rebuild, backup restoration, security architecture improvements',
        baseCost: 195000,
        isApplicable: true,
      },
      directLoss: {
        name: 'Direct Financial Loss',
        description: 'Ransom consideration ($8.2M demand), recovery costs, merchant compensation',
        baseCost: 750000,
        isApplicable: true,
      },
      lostRevenue: {
        name: 'Lost Revenue',
        description: 'Payment processing downtime, merchant churn',
        baseCost: 267000,
        isApplicable: true,
      },
      liabilityAndFines: {
        name: 'Liability & Fines',
        description: 'PCI-DSS penalties, regulatory fines, merchant lawsuits',
        baseCost: 105000,
        isApplicable: true,
      },
      brandDamage: {
        name: 'Brand & Reputation',
        description: 'Payment processor trust damage, competitive disadvantage',
        baseCost: 135000,
        isApplicable: true,
      },
    },
  },

  'cyber-crisis-initial': {
    incidentId: 'cyber-crisis-initial',
    incidentTitle: 'Major Data Breach Discovery',
    categories: {
      detectionAndEscalation: {
        name: 'Detection & Escalation',
        description: 'Extended forensic investigation, threat actor attribution',
        baseCost: 54000,
        isApplicable: true,
      },
      legal: {
        name: 'Legal Counsel',
        description: 'Multi-jurisdiction legal response, M&A disclosure implications',
        baseCost: 75000,
        isApplicable: true,
      },
      notification: {
        name: 'Notification',
        description: 'Mass customer notification, regulatory disclosure across regions',
        baseCost: 96000,
        isApplicable: true,
      },
      customerExperience: {
        name: 'Customer Experience',
        description: 'Credit monitoring services, identity protection for affected customers',
        baseCost: 255000,
        isApplicable: true,
      },
      postBreachResponse: {
        name: 'Post-Breach Response',
        description: 'Complete infrastructure rebuild, security transformation',
        baseCost: 360000,
        isApplicable: true,
      },
      directLoss: {
        name: 'Direct Financial Loss',
        description: 'IP theft impact, competitive intelligence loss',
        baseCost: 600000,
        isApplicable: true,
      },
      lostRevenue: {
        name: 'Lost Revenue',
        description: 'Customer churn, delayed M&A activities, market share loss',
        baseCost: 1050000,
        isApplicable: true,
      },
      liabilityAndFines: {
        name: 'Liability & Fines',
        description: 'GDPR fines, SEC penalties, class action settlements',
        baseCost: 570000,
        isApplicable: true,
      },
      brandDamage: {
        name: 'Brand & Reputation',
        description: 'Long-term brand damage, customer trust rebuilding',
        baseCost: 750000,
        isApplicable: true,
      },
    },
  },

  'insider-threat': {
    incidentId: 'insider-threat',
    incidentTitle: 'Insider Threat: Employee Whistleblowing',
    categories: {
      detectionAndEscalation: {
        name: 'Detection & Escalation',
        description: 'Internal investigation, document review, claims verification',
        baseCost: 29000,
        isApplicable: true,
      },
      legal: {
        name: 'Legal Counsel',
        description: 'Employment law, whistleblower protection compliance, media response',
        baseCost: 45000,
        isApplicable: true,
      },
      notification: {
        name: 'Notification',
        description: 'Board communication, stakeholder updates',
        baseCost: 8000,
        isApplicable: true,
      },
      customerExperience: {
        name: 'Customer Experience',
        description: 'Customer reassurance communications',
        baseCost: 14000,
        isApplicable: true,
      },
      postBreachResponse: {
        name: 'Post-Breach Response',
        description: 'Culture reform initiatives, governance improvements',
        baseCost: 105000,
        isApplicable: true,
      },
      directLoss: {
        name: 'Direct Financial Loss',
        description: 'Leadership transition costs, severance packages',
        baseCost: 150000,
        isApplicable: true,
      },
      lostRevenue: {
        name: 'Lost Revenue',
        description: 'Employee productivity loss, talent departure',
        baseCost: 120000,
        isApplicable: true,
      },
      liabilityAndFines: {
        name: 'Liability & Fines',
        description: 'Wrongful termination claims, regulatory scrutiny',
        baseCost: 51000,
        isApplicable: true,
      },
      brandDamage: {
        name: 'Brand & Reputation',
        description: 'Employer brand damage, leadership credibility loss',
        baseCost: 180000,
        isApplicable: true,
      },
    },
  },

  // ==========================================
  // PROJECT DENALI (Ransomware)
  // ==========================================
  'denali-initial-detection': {
    incidentId: 'denali-initial-detection',
    incidentTitle: 'Initial Detection: Helpdesk Reports',
    categories: {
      detectionAndEscalation: {
        name: 'Detection & Escalation',
        description: 'Initial triage, security team mobilization, CyberGuard MSSP engagement',
        baseCost: 5000,
        isApplicable: true,
      },
      legal: {
        name: 'Legal Counsel',
        description: 'Initial legal consultation, breach assessment',
        baseCost: 3000,
        isApplicable: true,
      },
      notification: {
        name: 'Notification',
        description: 'Internal escalation only at this stage',
        baseCost: 600,
        isApplicable: true,
      },
      customerExperience: {
        name: 'Customer Experience',
        description: 'N/A - Early detection phase',
        baseCost: 0,
        isApplicable: false,
      },
      postBreachResponse: {
        name: 'Post-Breach Response',
        description: 'Initial containment actions',
        baseCost: 8000,
        isApplicable: true,
      },
      directLoss: {
        name: 'Direct Financial Loss',
        description: 'Productivity loss from affected workstations',
        baseCost: 2400,
        isApplicable: true,
      },
      lostRevenue: {
        name: 'Lost Revenue',
        description: 'Minimal at detection stage',
        baseCost: 1800,
        isApplicable: true,
      },
      liabilityAndFines: {
        name: 'Liability & Fines',
        description: 'N/A - Too early for regulatory impact',
        baseCost: 0,
        isApplicable: false,
      },
      brandDamage: {
        name: 'Brand & Reputation',
        description: 'N/A - Incident not yet public',
        baseCost: 0,
        isApplicable: false,
      },
    },
  },

  'denali-escalation': {
    incidentId: 'denali-escalation',
    incidentTitle: 'Escalation: Ransomware Spreading',
    categories: {
      detectionAndEscalation: {
        name: 'Detection & Escalation',
        description: 'Full incident response activation, forensic team deployment',
        baseCost: 20000,
        isApplicable: true,
      },
      legal: {
        name: 'Legal Counsel',
        description: 'Breach counsel engagement, regulatory assessment',
        baseCost: 9000,
        isApplicable: true,
      },
      notification: {
        name: 'Notification',
        description: 'Executive notification, board awareness',
        baseCost: 2400,
        isApplicable: true,
      },
      customerExperience: {
        name: 'Customer Experience',
        description: 'Initial customer communication preparation',
        baseCost: 5000,
        isApplicable: true,
      },
      postBreachResponse: {
        name: 'Post-Breach Response',
        description: 'Containment actions, system isolation',
        baseCost: 36000,
        isApplicable: true,
      },
      directLoss: {
        name: 'Direct Financial Loss',
        description: 'Encrypted file servers, database impact',
        baseCost: 54000,
        isApplicable: true,
      },
      lostRevenue: {
        name: 'Lost Revenue',
        description: 'Tucson office operational disruption',
        baseCost: 29000,
        isApplicable: true,
      },
      liabilityAndFines: {
        name: 'Liability & Fines',
        description: 'Potential PCI impact assessment',
        baseCost: 9000,
        isApplicable: true,
      },
      brandDamage: {
        name: 'Brand & Reputation',
        description: 'Internal confidence impact',
        baseCost: 11000,
        isApplicable: true,
      },
    },
  },

  'denali-active-threat': {
    incidentId: 'denali-active-threat',
    incidentTitle: 'Active Threat: Lateral Movement Detected',
    categories: {
      detectionAndEscalation: {
        name: 'Detection & Escalation',
        description: 'Advanced threat hunting, credential analysis, AD investigation',
        baseCost: 29000,
        isApplicable: true,
      },
      legal: {
        name: 'Legal Counsel',
        description: 'Ongoing legal guidance, law enforcement coordination',
        baseCost: 15000,
        isApplicable: true,
      },
      notification: {
        name: 'Notification',
        description: 'Stakeholder updates, regulatory pre-notification',
        baseCost: 6000,
        isApplicable: true,
      },
      customerExperience: {
        name: 'Customer Experience',
        description: 'Customer service preparation, FAQ development',
        baseCost: 11000,
        isApplicable: true,
      },
      postBreachResponse: {
        name: 'Post-Breach Response',
        description: 'Credential reset operations, network microsegmentation',
        baseCost: 56000,
        isApplicable: true,
      },
      directLoss: {
        name: 'Direct Financial Loss',
        description: 'Expanded encryption impact, service account compromise',
        baseCost: 96000,
        isApplicable: true,
      },
      lostRevenue: {
        name: 'Lost Revenue',
        description: 'Payment processing delays, merchant impact',
        baseCost: 75000,
        isApplicable: true,
      },
      liabilityAndFines: {
        name: 'Liability & Fines',
        description: 'Growing regulatory exposure',
        baseCost: 21000,
        isApplicable: true,
      },
      brandDamage: {
        name: 'Brand & Reputation',
        description: 'Partner concern, market perception',
        baseCost: 45000,
        isApplicable: true,
      },
    },
  },

  'denali-infrastructure': {
    incidentId: 'denali-infrastructure',
    incidentTitle: 'Infrastructure Degradation: Domain Controllers Compromised',
    categories: {
      detectionAndEscalation: {
        name: 'Detection & Escalation',
        description: 'Major incident management, external IR team engagement',
        baseCost: 45000,
        isApplicable: true,
      },
      legal: {
        name: 'Legal Counsel',
        description: 'Full breach response legal team, regulatory liaison',
        baseCost: 33000,
        isApplicable: true,
      },
      notification: {
        name: 'Notification',
        description: 'Formal stakeholder notification, regulatory filing preparation',
        baseCost: 17000,
        isApplicable: true,
      },
      customerExperience: {
        name: 'Customer Experience',
        description: 'Customer communication, service status updates',
        baseCost: 26000,
        isApplicable: true,
      },
      postBreachResponse: {
        name: 'Post-Breach Response',
        description: 'AD rebuild, infrastructure restoration, security architecture',
        baseCost: 135000,
        isApplicable: true,
      },
      directLoss: {
        name: 'Direct Financial Loss',
        description: 'Enterprise-wide authentication failure impact',
        baseCost: 204000,
        isApplicable: true,
      },
      lostRevenue: {
        name: 'Lost Revenue',
        description: 'Complete payment processing disruption',
        baseCost: 156000,
        isApplicable: true,
      },
      liabilityAndFines: {
        name: 'Liability & Fines',
        description: 'Regulatory penalties, merchant compensation',
        baseCost: 51000,
        isApplicable: true,
      },
      brandDamage: {
        name: 'Brand & Reputation',
        description: 'Significant market confidence impact',
        baseCost: 120000,
        isApplicable: true,
      },
    },
  },

  'denali-exfiltration': {
    incidentId: 'denali-exfiltration',
    incidentTitle: 'Data Exfiltration Discovery',
    categories: {
      detectionAndEscalation: {
        name: 'Detection & Escalation',
        description: 'Forensic analysis, data flow investigation, scope determination',
        baseCost: 56000,
        isApplicable: true,
      },
      legal: {
        name: 'Legal Counsel',
        description: 'Data breach legal response, multi-jurisdiction coordination',
        baseCost: 51000,
        isApplicable: true,
      },
      notification: {
        name: 'Notification',
        description: 'Customer breach notification, regulatory filings',
        baseCost: 53000,
        isApplicable: true,
      },
      customerExperience: {
        name: 'Customer Experience',
        description: 'Credit monitoring, identity protection services',
        baseCost: 135000,
        isApplicable: true,
      },
      postBreachResponse: {
        name: 'Post-Breach Response',
        description: 'Evidence preservation, system hardening, security improvements',
        baseCost: 114000,
        isApplicable: true,
      },
      directLoss: {
        name: 'Direct Financial Loss',
        description: 'Data loss impact, competitive intelligence exposure',
        baseCost: 225000,
        isApplicable: true,
      },
      lostRevenue: {
        name: 'Lost Revenue',
        description: 'Banking partner revenue impact, customer churn',
        baseCost: 204000,
        isApplicable: true,
      },
      liabilityAndFines: {
        name: 'Liability & Fines',
        description: 'GDPR, state privacy laws, PCI-DSS penalties',
        baseCost: 111000,
        isApplicable: true,
      },
      brandDamage: {
        name: 'Brand & Reputation',
        description: 'Long-term trust damage, market position impact',
        baseCost: 165000,
        isApplicable: true,
      },
    },
  },

  'denali-root-cause': {
    incidentId: 'denali-root-cause',
    incidentTitle: 'Root Cause & Hardening',
    categories: {
      detectionAndEscalation: {
        name: 'Detection & Escalation',
        description: 'Root cause analysis, lessons learned documentation',
        baseCost: 14000,
        isApplicable: true,
      },
      legal: {
        name: 'Legal Counsel',
        description: 'Compliance gap remediation guidance',
        baseCost: 12000,
        isApplicable: true,
      },
      notification: {
        name: 'Notification',
        description: 'Final stakeholder reporting',
        baseCost: 5000,
        isApplicable: true,
      },
      customerExperience: {
        name: 'Customer Experience',
        description: 'Customer confidence rebuilding communications',
        baseCost: 11000,
        isApplicable: true,
      },
      postBreachResponse: {
        name: 'Post-Breach Response',
        description: 'Security program improvements, training, tool deployment',
        baseCost: 84000,
        isApplicable: true,
      },
      directLoss: {
        name: 'Direct Financial Loss',
        description: 'Hardening investment, tool procurement',
        baseCost: 56000,
        isApplicable: true,
      },
      lostRevenue: {
        name: 'Lost Revenue',
        description: 'Ongoing operational impact during hardening',
        baseCost: 29000,
        isApplicable: true,
      },
      liabilityAndFines: {
        name: 'Liability & Fines',
        description: 'Ongoing regulatory compliance costs',
        baseCost: 13000,
        isApplicable: true,
      },
      brandDamage: {
        name: 'Brand & Reputation',
        description: 'Reputation rebuilding investment',
        baseCost: 36000,
        isApplicable: true,
      },
    },
  },

  // ==========================================
  // PROJECT CASCADES (Cloud Misconfiguration)
  // ==========================================
  'cascades-initial-discovery': {
    incidentId: 'cascades-initial-discovery',
    incidentTitle: 'Initial Discovery: S3 Bucket Exposure',
    categories: {
      detectionAndEscalation: {
        name: 'Detection & Escalation',
        description: 'Weekend alert analysis, CloudTrail log review',
        baseCost: 8000,
        isApplicable: true,
      },
      legal: {
        name: 'Legal Counsel',
        description: 'Initial breach assessment, notification requirements',
        baseCost: 7000,
        isApplicable: true,
      },
      notification: {
        name: 'Notification',
        description: 'Internal escalation, IR team assembly',
        baseCost: 2400,
        isApplicable: true,
      },
      customerExperience: {
        name: 'Customer Experience',
        description: 'N/A - Discovery phase, no confirmed customer impact',
        baseCost: 0,
        isApplicable: false,
      },
      postBreachResponse: {
        name: 'Post-Breach Response',
        description: 'S3 bucket lockdown, access review',
        baseCost: 14000,
        isApplicable: true,
      },
      directLoss: {
        name: 'Direct Financial Loss',
        description: 'N/A - Exposure discovered, no confirmed theft',
        baseCost: 0,
        isApplicable: false,
      },
      lostRevenue: {
        name: 'Lost Revenue',
        description: 'Weekend response costs, on-call premium',
        baseCost: 5000,
        isApplicable: true,
      },
      liabilityAndFines: {
        name: 'Liability & Fines',
        description: 'N/A - No confirmed unauthorized access yet',
        baseCost: 0,
        isApplicable: false,
      },
      brandDamage: {
        name: 'Brand & Reputation',
        description: 'N/A - Internal discovery, not public',
        baseCost: 0,
        isApplicable: false,
      },
    },
  },

  'cascades-containment': {
    incidentId: 'cascades-containment',
    incidentTitle: 'Containment: IAM Exposure Assessment',
    categories: {
      detectionAndEscalation: {
        name: 'Detection & Escalation',
        description: 'IAM audit, access key exposure analysis',
        baseCost: 17000,
        isApplicable: true,
      },
      legal: {
        name: 'Legal Counsel',
        description: 'Data breach legal strategy, regulatory coordination',
        baseCost: 18000,
        isApplicable: true,
      },
      notification: {
        name: 'Notification',
        description: 'Banking partner notification, regulatory pre-filing',
        baseCost: 14000,
        isApplicable: true,
      },
      customerExperience: {
        name: 'Customer Experience',
        description: 'Merchant communication preparation',
        baseCost: 20000,
        isApplicable: true,
      },
      postBreachResponse: {
        name: 'Post-Breach Response',
        description: 'Credential rotation, IAM policy remediation',
        baseCost: 38000,
        isApplicable: true,
      },
      directLoss: {
        name: 'Direct Financial Loss',
        description: 'Exposed credentials impact, unauthorized access investigation',
        baseCost: 84000,
        isApplicable: true,
      },
      lostRevenue: {
        name: 'Lost Revenue',
        description: 'Potential payment processing impact',
        baseCost: 44000,
        isApplicable: true,
      },
      liabilityAndFines: {
        name: 'Liability & Fines',
        description: '2,300 customer records across 14 countries',
        baseCost: 45000,
        isApplicable: true,
      },
      brandDamage: {
        name: 'Brand & Reputation',
        description: 'Multi-jurisdiction trust impact',
        baseCost: 56000,
        isApplicable: true,
      },
    },
  },

  'cascades-broader-compromise': {
    incidentId: 'cascades-broader-compromise',
    incidentTitle: 'Broader Compromise: CI/CD Pipeline Access',
    categories: {
      detectionAndEscalation: {
        name: 'Detection & Escalation',
        description: 'CI/CD audit, Secrets Manager review, deployment history analysis',
        baseCost: 26000,
        isApplicable: true,
      },
      legal: {
        name: 'Legal Counsel',
        description: 'Payment gateway legal coordination, insurance notification',
        baseCost: 27000,
        isApplicable: true,
      },
      notification: {
        name: 'Notification',
        description: 'Third-party payment gateway notification',
        baseCost: 11000,
        isApplicable: true,
      },
      customerExperience: {
        name: 'Customer Experience',
        description: 'Transaction integrity verification communications',
        baseCost: 23000,
        isApplicable: true,
      },
      postBreachResponse: {
        name: 'Post-Breach Response',
        description: 'Secrets rotation, deployment audit, code review',
        baseCost: 59000,
        isApplicable: true,
      },
      directLoss: {
        name: 'Direct Financial Loss',
        description: 'Production database access exposure, API key rotation costs',
        baseCost: 126000,
        isApplicable: true,
      },
      lostRevenue: {
        name: 'Lost Revenue',
        description: 'CI/CD pipeline disruption, deployment freeze',
        baseCost: 56000,
        isApplicable: true,
      },
      liabilityAndFines: {
        name: 'Liability & Fines',
        description: 'Payment gateway compliance penalties',
        baseCost: 39000,
        isApplicable: true,
      },
      brandDamage: {
        name: 'Brand & Reputation',
        description: 'Engineering reputation, partner confidence',
        baseCost: 50000,
        isApplicable: true,
      },
    },
  },

  'cascades-production-impact': {
    incidentId: 'cascades-production-impact',
    incidentTitle: 'Production Impact: Payment Processing Disruption',
    categories: {
      detectionAndEscalation: {
        name: 'Detection & Escalation',
        description: 'Production monitoring, error analysis, impact assessment',
        baseCost: 14000,
        isApplicable: true,
      },
      legal: {
        name: 'Legal Counsel',
        description: 'SLA breach assessment, customer compensation analysis',
        baseCost: 18000,
        isApplicable: true,
      },
      notification: {
        name: 'Notification',
        description: 'Customer service disruption notification',
        baseCost: 17000,
        isApplicable: true,
      },
      customerExperience: {
        name: 'Customer Experience',
        description: '340 customers with failed transactions, priority support',
        baseCost: 38000,
        isApplicable: true,
      },
      postBreachResponse: {
        name: 'Post-Breach Response',
        description: 'Service restoration, Lambda fixes, RDS connection repair',
        baseCost: 50000,
        isApplicable: true,
      },
      directLoss: {
        name: 'Direct Financial Loss',
        description: '26% transaction failure rate, manual processing costs',
        baseCost: 114000,
        isApplicable: true,
      },
      lostRevenue: {
        name: 'Lost Revenue',
        description: 'Failed transactions, customer migration to competitors',
        baseCost: 86000,
        isApplicable: true,
      },
      liabilityAndFines: {
        name: 'Liability & Fines',
        description: 'SLA credits, regulatory penalties',
        baseCost: 30000,
        isApplicable: true,
      },
      brandDamage: {
        name: 'Brand & Reputation',
        description: 'Service reliability reputation damage',
        baseCost: 68000,
        isApplicable: true,
      },
    },
  },

  'cascades-root-cause': {
    incidentId: 'cascades-root-cause',
    incidentTitle: 'Root Cause & Hardening: Cloud Security Improvements',
    categories: {
      detectionAndEscalation: {
        name: 'Detection & Escalation',
        description: 'Post-incident review, root cause documentation',
        baseCost: 11000,
        isApplicable: true,
      },
      legal: {
        name: 'Legal Counsel',
        description: 'Compliance improvement guidance, insurance claims',
        baseCost: 11000,
        isApplicable: true,
      },
      notification: {
        name: 'Notification',
        description: 'Final stakeholder reporting, regulatory closure',
        baseCost: 6000,
        isApplicable: true,
      },
      customerExperience: {
        name: 'Customer Experience',
        description: 'Service restoration communication, trust rebuilding',
        baseCost: 14000,
        isApplicable: true,
      },
      postBreachResponse: {
        name: 'Post-Breach Response',
        description: 'IAM overhaul, IaC implementation, detection improvements',
        baseCost: 96000,
        isApplicable: true,
      },
      directLoss: {
        name: 'Direct Financial Loss',
        description: 'Security tooling investment, process changes',
        baseCost: 56000,
        isApplicable: true,
      },
      lostRevenue: {
        name: 'Lost Revenue',
        description: 'Engineering time diverted to hardening',
        baseCost: 38000,
        isApplicable: true,
      },
      liabilityAndFines: {
        name: 'Liability & Fines',
        description: 'Ongoing compliance monitoring costs',
        baseCost: 15000,
        isApplicable: true,
      },
      brandDamage: {
        name: 'Brand & Reputation',
        description: 'Long-term reputation rebuilding',
        baseCost: 44000,
        isApplicable: true,
      },
    },
  },

  // ==========================================
  // REDWOOD EXEC INCIDENTS (AI Agent)
  // ==========================================
  'redwood-exec-incident-1-escalation': {
    incidentId: 'redwood-exec-incident-1-escalation',
    incidentTitle: 'AI Agent Incident: Initial Escalation Decision',
    categories: {
      detectionAndEscalation: {
        name: 'Detection & Escalation',
        description: 'AI agent audit, voicemail analysis, affected patient identification',
        baseCost: 11000,
        isApplicable: true,
      },
      legal: {
        name: 'Legal Counsel',
        description: 'HIPAA breach assessment, notification requirements analysis',
        baseCost: 17000,
        isApplicable: true,
      },
      notification: {
        name: 'Notification',
        description: '312 patient notifications, HHS reporting preparation',
        baseCost: 14000,
        isApplicable: true,
      },
      customerExperience: {
        name: 'Customer/Patient Experience',
        description: 'Patient outreach, concern management',
        baseCost: 20000,
        isApplicable: true,
      },
      postBreachResponse: {
        name: 'Post-Breach Response',
        description: 'AI agent permissions review, S3 access controls',
        baseCost: 29000,
        isApplicable: true,
      },
      directLoss: {
        name: 'Direct Financial Loss',
        description: 'Twilio costs, immediate remediation',
        baseCost: 8000,
        isApplicable: true,
      },
      lostRevenue: {
        name: 'Lost Revenue',
        description: 'Executive time, operational disruption',
        baseCost: 17000,
        isApplicable: true,
      },
      liabilityAndFines: {
        name: 'Liability & Fines',
        description: 'Potential HIPAA penalties (312 patients)',
        baseCost: 42000,
        isApplicable: true,
      },
      brandDamage: {
        name: 'Brand & Reputation',
        description: 'Patient trust, AI adoption confidence',
        baseCost: 38000,
        isApplicable: true,
      },
    },
  },

  'redwood-exec-incident-2-notification': {
    incidentId: 'redwood-exec-incident-2-notification',
    incidentTitle: 'Breach Notification Decision',
    categories: {
      detectionAndEscalation: {
        name: 'Detection & Escalation',
        description: 'Scope confirmation, affected data classification',
        baseCost: 8000,
        isApplicable: true,
      },
      legal: {
        name: 'Legal Counsel',
        description: 'HIPAA notification strategy, HHS liaison',
        baseCost: 24000,
        isApplicable: true,
      },
      notification: {
        name: 'Notification',
        description: 'Patient notification letters, media response preparation',
        baseCost: 26000,
        isApplicable: true,
      },
      customerExperience: {
        name: 'Customer/Patient Experience',
        description: 'Call center staffing, patient support services',
        baseCost: 29000,
        isApplicable: true,
      },
      postBreachResponse: {
        name: 'Post-Breach Response',
        description: 'Credit monitoring enrollment, patient assistance program',
        baseCost: 44000,
        isApplicable: true,
      },
      directLoss: {
        name: 'Direct Financial Loss',
        description: 'Notification costs, support infrastructure',
        baseCost: 23000,
        isApplicable: true,
      },
      lostRevenue: {
        name: 'Lost Revenue',
        description: 'Patient attrition concern',
        baseCost: 56000,
        isApplicable: true,
      },
      liabilityAndFines: {
        name: 'Liability & Fines',
        description: 'OCR investigation preparation, state AG considerations',
        baseCost: 51000,
        isApplicable: true,
      },
      brandDamage: {
        name: 'Brand & Reputation',
        description: 'Healthcare provider trust, media coverage impact',
        baseCost: 68000,
        isApplicable: true,
      },
    },
  },

  'redwood-exec-incident-3-scope': {
    incidentId: 'redwood-exec-incident-3-scope',
    incidentTitle: 'Broader AI Agent Scope Assessment',
    categories: {
      detectionAndEscalation: {
        name: 'Detection & Escalation',
        description: 'Enterprise AI agent inventory, permission audit',
        baseCost: 20000,
        isApplicable: true,
      },
      legal: {
        name: 'Legal Counsel',
        description: 'AI governance legal framework, vendor liability',
        baseCost: 18000,
        isApplicable: true,
      },
      notification: {
        name: 'Notification',
        description: 'Board briefing, regulatory proactive disclosure',
        baseCost: 11000,
        isApplicable: true,
      },
      customerExperience: {
        name: 'Customer/Patient Experience',
        description: 'AI transparency communications',
        baseCost: 14000,
        isApplicable: true,
      },
      postBreachResponse: {
        name: 'Post-Breach Response',
        description: 'AI governance framework implementation',
        baseCost: 56000,
        isApplicable: true,
      },
      directLoss: {
        name: 'Direct Financial Loss',
        description: 'AI program pause, remediation investment',
        baseCost: 44000,
        isApplicable: true,
      },
      lostRevenue: {
        name: 'Lost Revenue',
        description: 'AI efficiency benefits delayed',
        baseCost: 68000,
        isApplicable: true,
      },
      liabilityAndFines: {
        name: 'Liability & Fines',
        description: 'Emerging AI regulation compliance',
        baseCost: 21000,
        isApplicable: true,
      },
      brandDamage: {
        name: 'Brand & Reputation',
        description: 'AI innovation reputation',
        baseCost: 50000,
        isApplicable: true,
      },
    },
  },

  'redwood-exec-incident-4-governance': {
    incidentId: 'redwood-exec-incident-4-governance',
    incidentTitle: 'AI Governance Framework Decision',
    categories: {
      detectionAndEscalation: {
        name: 'Detection & Escalation',
        description: 'Governance gap analysis, risk assessment',
        baseCost: 14000,
        isApplicable: true,
      },
      legal: {
        name: 'Legal Counsel',
        description: 'AI policy development, regulatory alignment',
        baseCost: 17000,
        isApplicable: true,
      },
      notification: {
        name: 'Notification',
        description: 'Stakeholder governance communications',
        baseCost: 8000,
        isApplicable: true,
      },
      customerExperience: {
        name: 'Customer/Patient Experience',
        description: 'Patient AI consent and transparency',
        baseCost: 17000,
        isApplicable: true,
      },
      postBreachResponse: {
        name: 'Post-Breach Response',
        description: 'AI oversight structure, monitoring implementation',
        baseCost: 68000,
        isApplicable: true,
      },
      directLoss: {
        name: 'Direct Financial Loss',
        description: 'Governance program investment',
        baseCost: 53000,
        isApplicable: true,
      },
      lostRevenue: {
        name: 'Lost Revenue',
        description: 'Slower AI deployment timeline',
        baseCost: 44000,
        isApplicable: true,
      },
      liabilityAndFines: {
        name: 'Liability & Fines',
        description: 'Future-proofing against AI regulations',
        baseCost: 17000,
        isApplicable: true,
      },
      brandDamage: {
        name: 'Brand & Reputation',
        description: 'Responsible AI leadership positioning',
        baseCost: 26000,
        isApplicable: true,
      },
    },
  },

  // ==========================================
  // GLACIER EXEC INCIDENTS (Healthcare Ransomware)
  // ==========================================
  'glacier-exec-incident-1-crisis': {
    incidentId: 'glacier-exec-incident-1-crisis',
    incidentTitle: 'Healthcare Ransomware: Executive Crisis Response',
    categories: {
      detectionAndEscalation: {
        name: 'Detection & Escalation',
        description: 'Incident command activation, executive briefing',
        baseCost: 23000,
        isApplicable: true,
      },
      legal: {
        name: 'Legal Counsel',
        description: 'HIPAA breach counsel, ransom payment legality',
        baseCost: 33000,
        isApplicable: true,
      },
      notification: {
        name: 'Notification',
        description: 'Board notification, regulatory pre-filing',
        baseCost: 14000,
        isApplicable: true,
      },
      customerExperience: {
        name: 'Customer/Patient Experience',
        description: 'Patient care continuity communications',
        baseCost: 38000,
        isApplicable: true,
      },
      postBreachResponse: {
        name: 'Post-Breach Response',
        description: 'Crisis management, containment coordination',
        baseCost: 86000,
        isApplicable: true,
      },
      directLoss: {
        name: 'Direct Financial Loss',
        description: '$2.5M ransom demand consideration, recovery costs',
        baseCost: 255000,
        isApplicable: true,
      },
      lostRevenue: {
        name: 'Lost Revenue',
        description: 'Healthcare service disruption',
        baseCost: 128000,
        isApplicable: true,
      },
      liabilityAndFines: {
        name: 'Liability & Fines',
        description: 'HIPAA penalties, patient care liability',
        baseCost: 93000,
        isApplicable: true,
      },
      brandDamage: {
        name: 'Brand & Reputation',
        description: 'Healthcare provider trust impact',
        baseCost: 116000,
        isApplicable: true,
      },
    },
  },

  'glacier-exec-incident-2-hipaa': {
    incidentId: 'glacier-exec-incident-2-hipaa',
    incidentTitle: 'HIPAA Breach Determination',
    categories: {
      detectionAndEscalation: {
        name: 'Detection & Escalation',
        description: 'PHI exposure assessment, breach determination',
        baseCost: 17000,
        isApplicable: true,
      },
      legal: {
        name: 'Legal Counsel',
        description: 'HIPAA breach notification compliance',
        baseCost: 27000,
        isApplicable: true,
      },
      notification: {
        name: 'Notification',
        description: 'Patient notification, HHS reporting, media response',
        baseCost: 56000,
        isApplicable: true,
      },
      customerExperience: {
        name: 'Customer/Patient Experience',
        description: 'Patient support line, credit monitoring',
        baseCost: 68000,
        isApplicable: true,
      },
      postBreachResponse: {
        name: 'Post-Breach Response',
        description: 'HIPAA remediation program',
        baseCost: 59000,
        isApplicable: true,
      },
      directLoss: {
        name: 'Direct Financial Loss',
        description: 'Notification costs, support infrastructure',
        baseCost: 50000,
        isApplicable: true,
      },
      lostRevenue: {
        name: 'Lost Revenue',
        description: 'Patient attrition, referral impact',
        baseCost: 86000,
        isApplicable: true,
      },
      liabilityAndFines: {
        name: 'Liability & Fines',
        description: 'OCR penalties, state AG actions',
        baseCost: 105000,
        isApplicable: true,
      },
      brandDamage: {
        name: 'Brand & Reputation',
        description: 'Healthcare provider reputation damage',
        baseCost: 98000,
        isApplicable: true,
      },
    },
  },

  'glacier-exec-incident-3-communication': {
    incidentId: 'glacier-exec-incident-3-communication',
    incidentTitle: 'Stakeholder Communication Strategy',
    categories: {
      detectionAndEscalation: {
        name: 'Detection & Escalation',
        description: 'Communication strategy development',
        baseCost: 8000,
        isApplicable: true,
      },
      legal: {
        name: 'Legal Counsel',
        description: 'Communications legal review',
        baseCost: 12000,
        isApplicable: true,
      },
      notification: {
        name: 'Notification',
        description: 'Multi-stakeholder communications program',
        baseCost: 29000,
        isApplicable: true,
      },
      customerExperience: {
        name: 'Customer/Patient Experience',
        description: 'Patient engagement, transparency program',
        baseCost: 26000,
        isApplicable: true,
      },
      postBreachResponse: {
        name: 'Post-Breach Response',
        description: 'Trust rebuilding initiatives',
        baseCost: 38000,
        isApplicable: true,
      },
      directLoss: {
        name: 'Direct Financial Loss',
        description: 'PR and communications investment',
        baseCost: 23000,
        isApplicable: true,
      },
      lostRevenue: {
        name: 'Lost Revenue',
        description: 'Reputation-related revenue impact',
        baseCost: 56000,
        isApplicable: true,
      },
      liabilityAndFines: {
        name: 'Liability & Fines',
        description: 'Communication compliance costs',
        baseCost: 9000,
        isApplicable: true,
      },
      brandDamage: {
        name: 'Brand & Reputation',
        description: 'Brand recovery investment',
        baseCost: 74000,
        isApplicable: true,
      },
    },
  },

  'glacier-exec-incident-4-recovery': {
    incidentId: 'glacier-exec-incident-4-recovery',
    incidentTitle: 'Recovery and Business Continuity',
    categories: {
      detectionAndEscalation: {
        name: 'Detection & Escalation',
        description: 'Recovery planning, prioritization',
        baseCost: 11000,
        isApplicable: true,
      },
      legal: {
        name: 'Legal Counsel',
        description: 'Recovery legal oversight, vendor contracts',
        baseCost: 11000,
        isApplicable: true,
      },
      notification: {
        name: 'Notification',
        description: 'Service restoration communications',
        baseCost: 11000,
        isApplicable: true,
      },
      customerExperience: {
        name: 'Customer/Patient Experience',
        description: 'Service continuity, patient care restoration',
        baseCost: 44000,
        isApplicable: true,
      },
      postBreachResponse: {
        name: 'Post-Breach Response',
        description: 'System rebuild, security enhancements',
        baseCost: 146000,
        isApplicable: true,
      },
      directLoss: {
        name: 'Direct Financial Loss',
        description: 'Infrastructure rebuild, security investment',
        baseCost: 116000,
        isApplicable: true,
      },
      lostRevenue: {
        name: 'Lost Revenue',
        description: 'Extended service disruption',
        baseCost: 98000,
        isApplicable: true,
      },
      liabilityAndFines: {
        name: 'Liability & Fines',
        description: 'Ongoing compliance requirements',
        baseCost: 21000,
        isApplicable: true,
      },
      brandDamage: {
        name: 'Brand & Reputation',
        description: 'Long-term reputation recovery',
        baseCost: 83000,
        isApplicable: true,
      },
    },
  },
};

// Cost templates for incident types (used for pattern matching)
// Scaled to mid-market benchmarks (~40% reduction from enterprise)
const costTemplates: Record<string, Omit<IncidentCostBreakdown, 'incidentId' | 'incidentTitle'>> = {
  // AI Agent Incidents (Redwood/Yosemite pattern)
  'ai-agent': {
    categories: {
      detectionAndEscalation: { name: 'Detection & Escalation', description: 'AI agent audit, behavior analysis, scope assessment', baseCost: 13000, isApplicable: true },
      legal: { name: 'Legal Counsel', description: 'AI liability assessment, regulatory guidance, vendor contracts', baseCost: 15000, isApplicable: true },
      notification: { name: 'Notification', description: 'Stakeholder notification, regulatory disclosure', baseCost: 17000, isApplicable: true },
      customerExperience: { name: 'Customer Experience', description: 'Customer/patient outreach, support services', baseCost: 23000, isApplicable: true },
      postBreachResponse: { name: 'Post-Breach Response', description: 'AI governance implementation, access controls', baseCost: 43000, isApplicable: true },
      directLoss: { name: 'Direct Financial Loss', description: 'Service costs, remediation investment', baseCost: 25000, isApplicable: true },
      lostRevenue: { name: 'Lost Revenue', description: 'Operational disruption, delayed initiatives', baseCost: 37000, isApplicable: true },
      liabilityAndFines: { name: 'Liability & Fines', description: 'Regulatory penalties, compliance costs', baseCost: 27000, isApplicable: true },
      brandDamage: { name: 'Brand & Reputation', description: 'AI trust impact, innovation reputation', baseCost: 43000, isApplicable: true },
    },
  },

  // Healthcare Ransomware (Glacier/Rainier pattern)
  'healthcare-ransomware': {
    categories: {
      detectionAndEscalation: { name: 'Detection & Escalation', description: 'Incident response, forensic investigation', baseCost: 37000, isApplicable: true },
      legal: { name: 'Legal Counsel', description: 'HIPAA compliance, breach notification, ransom guidance', baseCost: 27000, isApplicable: true },
      notification: { name: 'Notification', description: 'Patient notification, HHS reporting', baseCost: 43000, isApplicable: true },
      customerExperience: { name: 'Customer/Patient Experience', description: 'Patient support, credit monitoring', baseCost: 67000, isApplicable: true },
      postBreachResponse: { name: 'Post-Breach Response', description: 'System rebuild, security enhancements', baseCost: 115000, isApplicable: true },
      directLoss: { name: 'Direct Financial Loss', description: 'Recovery costs, ransom consideration', baseCost: 195000, isApplicable: true },
      lostRevenue: { name: 'Lost Revenue', description: 'Healthcare service disruption', baseCost: 127000, isApplicable: true },
      liabilityAndFines: { name: 'Liability & Fines', description: 'HIPAA penalties, patient lawsuits', baseCost: 81000, isApplicable: true },
      brandDamage: { name: 'Brand & Reputation', description: 'Healthcare provider trust damage', baseCost: 97000, isApplicable: true },
    },
  },

  // Cloud Misconfiguration (Olympic/Rainier/Azure pattern)
  'cloud-misconfiguration': {
    categories: {
      detectionAndEscalation: { name: 'Detection & Escalation', description: 'Cloud audit, IAM review, exposure assessment', baseCost: 17000, isApplicable: true },
      legal: { name: 'Legal Counsel', description: 'Data breach legal response, regulatory coordination', baseCost: 18000, isApplicable: true },
      notification: { name: 'Notification', description: 'Customer notification, regulatory filings', baseCost: 19000, isApplicable: true },
      customerExperience: { name: 'Customer Experience', description: 'Customer support, impact communications', baseCost: 25000, isApplicable: true },
      postBreachResponse: { name: 'Post-Breach Response', description: 'IAM remediation, security hardening', baseCost: 49000, isApplicable: true },
      directLoss: { name: 'Direct Financial Loss', description: 'Data exposure impact, credential rotation', baseCost: 85000, isApplicable: true },
      lostRevenue: { name: 'Lost Revenue', description: 'Service disruption, customer churn', baseCost: 59000, isApplicable: true },
      liabilityAndFines: { name: 'Liability & Fines', description: 'Regulatory penalties, compliance costs', baseCost: 36000, isApplicable: true },
      brandDamage: { name: 'Brand & Reputation', description: 'Cloud security reputation', baseCost: 53000, isApplicable: true },
    },
  },

  // Executive Crisis Management (Yellowstone/Shenandoah exec pattern)
  'executive-crisis': {
    categories: {
      detectionAndEscalation: { name: 'Detection & Escalation', description: 'Crisis assessment, executive briefing', baseCost: 11000, isApplicable: true },
      legal: { name: 'Legal Counsel', description: 'Crisis legal strategy, regulatory liaison', baseCost: 21000, isApplicable: true },
      notification: { name: 'Notification', description: 'Board notification, stakeholder communications', baseCost: 13000, isApplicable: true },
      customerExperience: { name: 'Customer Experience', description: 'Customer confidence communications', baseCost: 19000, isApplicable: true },
      postBreachResponse: { name: 'Post-Breach Response', description: 'Crisis resolution, governance improvements', baseCost: 55000, isApplicable: true },
      directLoss: { name: 'Direct Financial Loss', description: 'Crisis management costs, operational impact', baseCost: 67000, isApplicable: true },
      lostRevenue: { name: 'Lost Revenue', description: 'Business disruption, opportunity cost', baseCost: 85000, isApplicable: true },
      liabilityAndFines: { name: 'Liability & Fines', description: 'Regulatory exposure, compliance penalties', baseCost: 29000, isApplicable: true },
      brandDamage: { name: 'Brand & Reputation', description: 'Executive leadership reputation', baseCost: 73000, isApplicable: true },
    },
  },

  // Technical Incident Response (Shenandoah tech, Arches, Sequoia pattern)
  'technical-incident': {
    categories: {
      detectionAndEscalation: { name: 'Detection & Escalation', description: 'Technical investigation, threat analysis', baseCost: 19000, isApplicable: true },
      legal: { name: 'Legal Counsel', description: 'Compliance assessment, vendor coordination', baseCost: 13000, isApplicable: true },
      notification: { name: 'Notification', description: 'Internal escalation, stakeholder updates', baseCost: 7000, isApplicable: true },
      customerExperience: { name: 'Customer Experience', description: 'Service status communications', baseCost: 13000, isApplicable: true },
      postBreachResponse: { name: 'Post-Breach Response', description: 'Technical remediation, security hardening', baseCost: 47000, isApplicable: true },
      directLoss: { name: 'Direct Financial Loss', description: 'System impact, recovery costs', baseCost: 55000, isApplicable: true },
      lostRevenue: { name: 'Lost Revenue', description: 'Operational disruption', baseCost: 43000, isApplicable: true },
      liabilityAndFines: { name: 'Liability & Fines', description: 'Compliance penalties', baseCost: 18000, isApplicable: true },
      brandDamage: { name: 'Brand & Reputation', description: 'Technical reputation impact', baseCost: 29000, isApplicable: true },
    },
  },
};

// Pattern matching for incident IDs to templates
function getTemplateForIncident(incidentId: string): string | null {
  // AI Agent patterns
  if (incidentId.includes('redwood') || incidentId.includes('yosemite') ||
      incidentId.includes('twilio') || incidentId.includes('data-agent') ||
      incidentId.includes('cost-spike') || incidentId.includes('engineering-containment')) {
    return 'ai-agent';
  }

  // Healthcare Ransomware patterns
  if (incidentId.includes('glacier') || incidentId.includes('hipaa') ||
      (incidentId.includes('firsthealth') && incidentId.includes('ransomware'))) {
    return 'healthcare-ransomware';
  }

  // Cloud Misconfiguration patterns
  if (incidentId.includes('olympic') || incidentId.includes('rainier') ||
      incidentId.includes('azure') || incidentId.includes('cloud') ||
      incidentId.includes('s3') || incidentId.includes('iam')) {
    return 'cloud-misconfiguration';
  }

  // Executive Crisis patterns
  if (incidentId.includes('yellowstone') ||
      (incidentId.includes('shenandoah') && !incidentId.includes('tech')) ||
      incidentId.includes('exec') || incidentId.includes('crisis') ||
      incidentId.includes('media') || incidentId.includes('communications') ||
      incidentId.includes('compliance') || incidentId.includes('regulatory')) {
    return 'executive-crisis';
  }

  // Technical Incident patterns
  if (incidentId.includes('tech') || incidentId.includes('arches') ||
      incidentId.includes('sequoia') || incidentId.includes('infrastructure') ||
      incidentId.includes('credential') || incidentId.includes('lateral') ||
      incidentId.includes('persistence') || incidentId.includes('hardening') ||
      incidentId.includes('vuln') || incidentId.includes('identity') ||
      incidentId.includes('supply-chain') || incidentId.includes('data-migration')) {
    return 'technical-incident';
  }

  return null;
}

// Helper to get cost breakdown for an incident
export function getIncidentCostBreakdown(incidentId: string): IncidentCostBreakdown | null {
  // First check for exact match in explicit data
  if (incidentCostData[incidentId]) {
    return incidentCostData[incidentId];
  }

  // Fall back to pattern-based template
  const templateKey = getTemplateForIncident(incidentId);
  if (templateKey && costTemplates[templateKey]) {
    const template = costTemplates[templateKey];
    return {
      incidentId,
      incidentTitle: incidentId.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
      categories: template.categories,
    };
  }

  return null;
}
